import { Task, TaskComment } from "../../services/taskService";

/**
 * Who may do what with team tasks. The API enforces these rules
 * (api/app/services/task_permissions.py); this copy only decides which
 * controls the admin offers — change both together.
 *
 * - The whole team can see every task and comment on it.
 * - A task's creator and its assignee can change it.
 * - Anyone else can change it only if they're an Owner.
 * - Only Owners can delete a task — not even the person who created it.
 * - A comment can be edited by its author, and deleted by its author or
 *   an Owner.
 */

/** The signed-in staff member, as far as task rights go. */
export interface Viewer {
    userId: string | undefined;
    role: string | undefined;
}

export interface TaskRights {
    canEdit: boolean;
    canDelete: boolean;
}

type TaskPeople = Pick<Task, "created_by_user_id" | "assignee_user_id">;
type CommentAuthor = Pick<TaskComment, "author_user_id">;

/** Shown where a control is off — the same wording the API answers with. */
export const PERMISSION_TEXT = {
    edit: "Only this task's creator, its assignee or an Owner can change it.",
    complete: "Only this task's creator, its assignee or an Owner can complete it.",
    delete: "Only an Owner can delete tasks.",
} as const;

export const isOwner = (viewer: Viewer): boolean => viewer.role === "OWNER";

/** The task's creator or its assignee. */
export const isOnTask = (task: TaskPeople, viewer: Viewer): boolean =>
    !!viewer.userId && (task.created_by_user_id === viewer.userId || task.assignee_user_id === viewer.userId);

export const canEditTask = (task: TaskPeople, viewer: Viewer): boolean => isOnTask(task, viewer) || isOwner(viewer);

/** Owners only, whoever created the task. */
export const canDeleteTask = (viewer: Viewer): boolean => isOwner(viewer);

export const canEditComment = (comment: CommentAuthor, viewer: Viewer): boolean =>
    !!viewer.userId && comment.author_user_id === viewer.userId;

export const canDeleteComment = (comment: CommentAuthor, viewer: Viewer): boolean =>
    canEditComment(comment, viewer) || isOwner(viewer);

export const taskRights = (task: TaskPeople, viewer: Viewer): TaskRights => ({
    canEdit: canEditTask(task, viewer),
    canDelete: canDeleteTask(viewer),
});

export const NO_RIGHTS: TaskRights = { canEdit: false, canDelete: false };
